import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Avatar,
  Card,
  CardContent,
  TextField,
  Stack,
  Divider,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  createIdentityAPIEndpoint,
  EndPoints,
} from "../../services/AuthServices";
import { enqueueSnackbar } from "notistack";
import { ArrowBackOutlined, LockOutlined, PhotoCameraBackOutlined, PhotoCameraOutlined, SaveOutlined } from "@mui/icons-material";

const EditProfile = () => {
  const navigate = useNavigate();

  const [student, setStudent] = useState({
    userName: "",
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [passwordErrors, setPasswordErrors] = useState({});

  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchAndDisplayImage = async (imageName) => {
    try {
      const response = await createIdentityAPIEndpoint(
        EndPoints.getStudentImage(imageName),
      ).fetchWithOptions({
        responseType: "blob",
      });

      const imageUrl = URL.createObjectURL(response.data);
      setPreviewUrl(imageUrl);
    } catch (error) {
      console.error("Error fetching image:", error);
    }
  };

  useEffect(() => {
    const getProfile = async () => {
      try {
        const res = await createIdentityAPIEndpoint(EndPoints.details).fetch();

        setStudent(res.data);

        if (res.data.imageName) {
          fetchAndDisplayImage(res.data.imageName);
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
      }
    };

    getProfile();
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];

    if (file) {
      setImageFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleChangeInput = (e) => {
    const { name, value } = e.target;

    setStudent((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;

    setPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validatePassword = () => {
    const errors = {};

    const { currentPassword, newPassword, confirmPassword } = passwordData;

    // Password change is optional
    if (!currentPassword && !newPassword && !confirmPassword) {
      return errors;
    }

    if (!currentPassword) {
      errors.currentPassword = "Current password is required.";
    }

    if (!newPassword) {
      errors.newPassword = "New password is required.";
    } else if (newPassword.length < 6) {
      errors.newPassword = "Password must be at least 6 characters.";
    }

    if (!confirmPassword) {
      errors.confirmPassword = "Please confirm your new password.";
    } else if (newPassword !== confirmPassword) {
      errors.confirmPassword = "Passwords do not match.";
    }

    setPasswordErrors(errors);

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validatePassword();

    if (Object.keys(errors).length > 0) {
      return;
    }

    setLoading(true);

    try {
      // =========================
      // Upload Image
      // =========================

      if (imageFile) {
        const formData = new FormData();

        formData.append("image", imageFile);

        await createIdentityAPIEndpoint(EndPoints.uploadImage).postWithToken(
          formData,
        );

        enqueueSnackbar("Image uploaded successfully!", {
          variant: "success",
          style: {
            fontSize: "16px",
          },
        });
      }

      // =========================
      // Update Profile
      // =========================

      const profileData = {
        userName: student.userName,
      };

      // Add password only if user wants to change it
      if (passwordData.newPassword) {
        profileData.currentPassword = passwordData.currentPassword;

        profileData.newPassword = passwordData.newPassword;
      }

      await createIdentityAPIEndpoint(EndPoints.editProfile).putWithToken(
        profileData,
      );

      enqueueSnackbar("Profile updated successfully!", {
        variant: "success",
        style: {
          fontSize: "16px",
        },
      });

      navigate("/my-profile");
    } catch (error) {
      console.error("Error updating profile:", error);

      const message =
        error.response?.data?.message || "Failed to update profile.";

      enqueueSnackbar(message, {
        variant: "error",
        style: {
          fontSize: "16px",
        },
      });
    } finally {
      setLoading(false);
    }
  };

  if (!student) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <section className="edit-profile section">
      {" "}
      <div className="profile-container">
        {" "}
        <div className="profile-heading edit-heading">
          {" "}
          <span className="profile-badge">✦ إعدادات الحساب</span>{" "}
          <h1>
            {" "}
            تعديل <span>بياناتك</span>{" "}
          </h1>{" "}
          <p> حدّث صورتك أو اسم المستخدم أو كلمة المرور بسهولة. </p>{" "}
        </div>{" "}
        <Card className="profile-card edit-card">
          {" "}
          <CardContent className="edit-card-content">
            {" "}
            <form onSubmit={handleSubmit}>
              {" "}
              {/* Profile Image */}{" "}
              <div className="edit-image-section">
                {" "}
                <div className="edit-image-wrapper">
                  {" "}
                  <Avatar
                    src={previewUrl}
                    alt={student?.fullName || "صورة الطالب"}
                    className="edit-avatar"
                  />{" "}
                </div>{" "}
                <div className="edit-image-content">
                  {" "}
                  <h3>الصورة الشخصية</h3>{" "}
                  <p> اختار صورة واضحة ليك، ويفضل تكون مربعة. </p>{" "}
                  <label htmlFor="file-upload" className="profile-upload-btn">
                    {" "}
                    <PhotoCameraBackOutlined /> تغيير الصورة{" "}
                  </label>{" "}
                  <input
                    id="file-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="profile-file-input"
                  />{" "}
                </div>{" "}
              </div>{" "}
              <Divider className="profile-divider" />{" "}
              {/* Account Information */}{" "}
              <div className="edit-section-heading">
                {" "}
                <div className="edit-section-icon"> 👤 </div>{" "}
                <div>
                  {" "}
                  <h3>بيانات الحساب</h3>{" "}
                  <p>عدّل اسم المستخدم الخاص بيك.</p>{" "}
                </div>{" "}
              </div>{" "}
              <TextField
                fullWidth
                variant="outlined"
                label="اسم المستخدم"
                name="userName"
                value={student?.userName || ""}
                onChange={handleChangeInput}
                className="profile-input"
                margin="normal"
              />{" "}
              <Divider className="profile-divider" /> {/* Password */}{" "}
              <div className="edit-section-heading">
                {" "}
                <div className="edit-section-icon password-icon">
                  {" "}
                  <LockOutlined />{" "}
                </div>{" "}
                <div>
                  {" "}
                  <h3>تغيير كلمة المرور</h3>{" "}
                  <p> اكتب كلمة المرور الحالية ثم كلمة المرور الجديدة. </p>{" "}
                </div>{" "}
              </div>{" "}
              <Stack spacing={2} className="password-fields">
                {" "}
                <TextField
                  fullWidth
                  type="password"
                  label="كلمة المرور الحالية"
                  name="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  error={!!passwordErrors.currentPassword}
                  helperText={passwordErrors.currentPassword}
                />{" "}
                <TextField
                  fullWidth
                  type="password"
                  label="كلمة المرور الجديدة"
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  error={!!passwordErrors.newPassword}
                  helperText={passwordErrors.newPassword}
                />{" "}
                <TextField
                  fullWidth
                  type="password"
                  label="تأكيد كلمة المرور الجديدة"
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  error={!!passwordErrors.confirmPassword}
                  helperText={passwordErrors.confirmPassword}
                />{" "}
              </Stack>{" "}
              {/* Actions */}{" "}
              <div className="edit-actions">
                {" "}
                <Button
                  type="submit"
                  variant="contained"
                  className="profile-primary-btn"
                  disabled={loading}
                  startIcon={<SaveOutlined />}
                >
                  {" "}
                  {loading ? "جاري الحفظ..." : "حفظ التعديلات"}{" "}
                </Button>{" "}
                <Button
                  type="button"
                  variant="outlined"
                  className="profile-secondary-btn"
                  onClick={() => navigate("/my-profile")}
                  startIcon={<ArrowBackOutlined />}
                >
                  {" "}
                  رجوع للملف الشخصي{" "}
                </Button>{" "}
              </div>{" "}
            </form>{" "}
          </CardContent>{" "}
        </Card>{" "}
      </div>{" "}
    </section>
  );
};

export default EditProfile;
